/**
 * AdminAppVersion.js — Mobil Uygulama Sürüm Yönetimi (force-update kapısı admin arayüzü)
 *
 * Route: /admin/app-version (system nav grubu — notification-jobs'ın hemen altı)
 *
 * API (bkz. backend/routes/app_version.py — gerçek şema, uydurulmadı):
 *   GET  /admin/app-version              → { items: [ {...android}, {...ios} ] }
 *   PUT  /admin/app-version/{platform}   → günceller (upsert), tam config'i döner
 *
 * Kritik davranış: min_supported_version değiştiğinde bu sürümün ALTINDAKİ TÜM
 * kullanıcılar force-update kapısında (GET /app/version-check → update_required)
 * anında kilitlenir. Bu yüzden:
 *   - min_supported_version alanının hemen altında SÜREKLİ görünen bir uyarı var.
 *   - Kaydetmeden önce, min_supported_version değiştiyse admin yeni sürümü
 *     tekrar yazarak teyit etmek zorunda (bkz. LockConfirmDialog).
 *   - Semver formatı ve min<=latest kuralı client'ta da anında doğrulanır
 *     (backend/utils/semver.py ile birebir aynı regex) — backend zaten 422 ile
 *     reddeder ama kullanıcı submit etmeden önce görsün istiyoruz.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Smartphone, AlertTriangle, Save, Loader2 } from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';
import { PageHeader, FormSection, FormField, EmptyState, LoadingState, VersionDistributionCard } from '../components/admin';
import BilingualTextField from '../components/admin/BilingualTextField';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../components/ui/alert-dialog';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { fmtDateOnly } from '../utils/formatDateOnly';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLATFORMS = ['android', 'ios'];

// ── Semver — backend/utils/semver.py::_SEMVER_RE ile birebir aynı kural ──────
// Gecerli: "1", "1.0", "1.0.0" — pre-release/build suffix desteklenmez.
const SEMVER_RE = /^\d+(?:\.\d+)?(?:\.\d+)?$/;

function isValidSemver(v) {
  return typeof v === 'string' && SEMVER_RE.test(v.trim());
}

// ── store_url — backend/routes/app_version.py::STORE_URL_ALLOWED_HOSTS ile
// BİREBİR senkron tutulmalı. Bu URL, mobil uygulamada zorunlu güncelleme
// (hard gate) ekranındaki TEK çıkış butonuna Linking.openURL ile bağlanır —
// kullanıcı bu ekranı kapatamaz, bu yüzden değer sadece bilinen mağaza/domain
// host'larına ve https şemasına izin verilerek kısıtlanır (açık yönlendirme /
// phishing engelleme).
const STORE_URL_ALLOWED_HOSTS = new Set([
  'play.google.com',
  'apps.apple.com',
  'itunes.apple.com',
  'kariyeronline.tr',
  'www.kariyeronline.tr',
]);

function isValidStoreUrl(v) {
  const trimmed = (v || '').trim();
  if (!trimmed) return true; // boş serbest — iOS için henüz mağaza linki olmayabilir (null gecerli)
  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  return STORE_URL_ALLOWED_HOSTS.has(parsed.hostname.toLowerCase());
}

function parseSemver(v) {
  if (!isValidSemver(v)) return null;
  const [major, minor, patch] = v.trim().split('.').map(Number);
  return [major || 0, minor || 0, patch || 0];
}

function compareSemver(a, b) {
  const pa = parseSemver(a);
  const pb = parseSemver(b);
  if (!pa || !pb) return 0;
  for (let i = 0; i < 3; i += 1) {
    if (pa[i] < pb[i]) return -1;
    if (pa[i] > pb[i]) return 1;
  }
  return 0;
}

function toFormState(cfg) {
  return {
    min_supported_version: cfg?.min_supported_version || '',
    latest_version: cfg?.latest_version || '',
    store_url: cfg?.store_url || '',
    title_tr: cfg?.title?.tr || '',
    title_en: cfg?.title?.en || '',
    message_tr: cfg?.message?.tr || '',
    message_en: cfg?.message?.en || '',
    release_notes_tr: cfg?.release_notes?.tr || '',
    release_notes_en: cfg?.release_notes?.en || '',
  };
}

function cleanStr(s) {
  const trimmed = (s || '').trim();
  return trimmed || null;
}

function toPayload(form) {
  return {
    min_supported_version: form.min_supported_version.trim(),
    latest_version: form.latest_version.trim(),
    store_url: cleanStr(form.store_url),
    title: { tr: cleanStr(form.title_tr), en: cleanStr(form.title_en) },
    message: { tr: cleanStr(form.message_tr), en: cleanStr(form.message_en) },
    release_notes: { tr: cleanStr(form.release_notes_tr), en: cleanStr(form.release_notes_en) },
  };
}

function fmtDate(iso, lang) {
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  try {
    return dt.toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US');
  } catch {
    return null;
  }
}

// ── Kilit onay diyaloğu — yeni min sürümü tekrar yazdırarak teyit alır ────────
function LockConfirmDialog({ open, onOpenChange, newVersion, onConfirm, loading, t }) {
  const [typed, setTyped] = useState('');
  const isMatch = typed.trim() === (newVersion || '').trim() && Boolean(newVersion);

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-2xl border-2 border-[var(--border-light)] max-w-md">
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            <div className="mt-0.5 text-rose-600">
              <AlertTriangle size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <AlertDialogTitle className="font-display text-lg font-bold text-[var(--text-main)]">
                {t('admin.appVersion.confirmTitle')}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="mt-2 space-y-3">
                  <p className="text-sm text-[var(--text-muted)]">
                    {t('admin.appVersion.confirmBody').replace('{{version}}', newVersion || '')}
                  </p>
                  <div className="space-y-1">
                    <Label
                      htmlFor="app-version-confirm-input"
                      className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide"
                    >
                      {t('admin.appVersion.confirmInputLabel')}
                    </Label>
                    <Input
                      id="app-version-confirm-input"
                      value={typed}
                      onChange={(e) => setTyped(e.target.value)}
                      placeholder={newVersion || ''}
                      className="border-2 rounded-xl font-mono text-sm"
                      autoComplete="off"
                      autoFocus
                      data-testid="app-version-lock-confirm-input"
                    />
                    {typed.length > 0 && !isMatch && (
                      <p className="text-xs text-rose-600 font-semibold" role="alert">
                        {t('admin.appVersion.confirmMismatch').replace('{{version}}', newVersion || '')}
                      </p>
                    )}
                  </div>
                </div>
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 mt-2">
          <AlertDialogCancel className="rounded-xl border-2 border-[var(--border-heavy)] font-bold h-9" disabled={loading}>
            {t('admin.appVersion.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            className="h-9 bg-rose-600 hover:bg-rose-700 text-white border-rose-800 border-b-4 rounded-xl font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={onConfirm}
            disabled={!isMatch || loading}
            data-testid="app-version-lock-confirm-btn"
          >
            {loading ? (
              <span className="flex items-center gap-1.5">
                <Loader2 size={14} className="animate-spin" />
                {t('admin.appVersion.saving')}
              </span>
            ) : (
              t('admin.appVersion.confirmButton')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ── Platform sekmesi içeriği (Android / iOS) ──────────────────────────────────
function PlatformPanel({
  platform, config, form, onFieldChange, onSaveClick, saving, t, lang,
  statsWindowDays, onStatsWindowChange, statsFromDate, statsToDate,
  statsLoading, statsError, onStatsRetry, platformStats,
  onFillSuggested, staleFillPending, staleFillRing,
}) {
  const minInvalid = !isValidSemver(form.min_supported_version);
  const latestInvalid = !isValidSemver(form.latest_version);
  const minGtLatest = !minInvalid && !latestInvalid
    && compareSemver(form.min_supported_version, form.latest_version) > 0;

  const minError = minInvalid
    ? t('admin.appVersion.errorInvalidSemver')
    : (minGtLatest ? t('admin.appVersion.errorMinGtLatest') : null);
  const latestError = latestInvalid ? t('admin.appVersion.errorInvalidSemver') : null;
  const storeUrlError = isValidStoreUrl(form.store_url) ? null : t('admin.appVersion.errorInvalidStoreUrl');
  const hasErrors = Boolean(minError || latestError || storeUrlError);

  const lastUpdated = fmtDate(config?.updated_at, lang);

  return (
    <div className="space-y-4">
      <FormSection
        title={t('admin.appVersion.sectionVersions')}
        description={t('admin.appVersion.sectionVersionsDesc')}
        columns={2}
      >
        <FormField
          label={t('admin.appVersion.fieldMinVersion')}
          description={t('admin.appVersion.fieldMinVersionHelp')}
          required
          error={minError}
        >
          <Input
            value={form.min_supported_version}
            onChange={(e) => onFieldChange('min_supported_version', e.target.value)}
            placeholder="1.2.0"
            className={minError ? 'border-rose-400 focus-visible:ring-rose-400' : ''}
            data-testid={`app-version-min-${platform}`}
          />
        </FormField>
        <FormField
          label={t('admin.appVersion.fieldLatestVersion')}
          description={t('admin.appVersion.fieldLatestVersionHelp')}
          required
          error={latestError}
        >
          <Input
            value={form.latest_version}
            onChange={(e) => onFieldChange('latest_version', e.target.value)}
            placeholder="1.3.0"
            className={
              latestError
                ? 'border-rose-400 focus-visible:ring-rose-400'
                : (staleFillRing ? 'ring-2 ring-amber-400 transition-shadow duration-500' : '')
            }
            data-testid={`app-version-latest-${platform}`}
          />
        </FormField>
        <FormField
          label={t('admin.appVersion.fieldStoreUrl')}
          description={t('admin.appVersion.fieldStoreUrlHint')}
          error={storeUrlError}
          span={2}
        >
          <Input
            value={form.store_url}
            onChange={(e) => onFieldChange('store_url', e.target.value)}
            placeholder={t('admin.appVersion.fieldStoreUrlPlaceholder')}
            className={storeUrlError ? 'border-rose-400 focus-visible:ring-rose-400' : ''}
            data-testid={`app-version-store-url-${platform}`}
          />
        </FormField>
      </FormSection>

      {/* Kritik uyarı — min_supported_version'ın etkisi her zaman görünür, sadece diyalogda değil */}
      <div className="flex items-start gap-3 bg-rose-50 border-2 border-rose-200 rounded-2xl p-4">
        <AlertTriangle size={20} className="text-rose-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-rose-700">{t('admin.appVersion.warningLockTitle')}</p>
          <p className="text-xs text-rose-600 mt-0.5">
            {t('admin.appVersion.warningLockBody').replace(
              '{{version}}',
              form.min_supported_version?.trim() || '—',
            )}
          </p>
        </div>
      </div>

      <VersionDistributionCard
        platform={platform}
        windowDays={statsWindowDays}
        onWindowChange={onStatsWindowChange}
        fromDate={statsFromDate}
        toDate={statsToDate}
        loading={statsLoading}
        error={statsError}
        onRetry={onStatsRetry}
        data={platformStats}
        onFillSuggested={(version) => onFillSuggested(platform, version)}
        fillPending={staleFillPending}
        t={t}
        lang={lang}
      />

      <FormSection
        title={t('admin.appVersion.sectionOverride')}
        description={t('admin.appVersion.sectionOverrideDesc')}
        columns={1}
      >
        <BilingualTextField
          labelTr={`${t('admin.appVersion.fieldTitle')} (TR)`}
          labelEn={`${t('admin.appVersion.fieldTitle')} (EN)`}
          nameTr="title_tr"
          nameEn="title_en"
          valueTr={form.title_tr}
          valueEn={form.title_en}
          onChange={onFieldChange}
        />
        <BilingualTextField
          labelTr={`${t('admin.appVersion.fieldMessage')} (TR)`}
          labelEn={`${t('admin.appVersion.fieldMessage')} (EN)`}
          nameTr="message_tr"
          nameEn="message_en"
          valueTr={form.message_tr}
          valueEn={form.message_en}
          onChange={onFieldChange}
          multiline
          rows={2}
        />
        <BilingualTextField
          labelTr={`${t('admin.appVersion.fieldReleaseNotes')} (TR)`}
          labelEn={`${t('admin.appVersion.fieldReleaseNotes')} (EN)`}
          nameTr="release_notes_tr"
          nameEn="release_notes_en"
          valueTr={form.release_notes_tr}
          valueEn={form.release_notes_en}
          onChange={onFieldChange}
          multiline
          rows={3}
        />
      </FormSection>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <span className="text-xs text-[var(--text-muted)]">
          {lastUpdated
            ? `${t('admin.appVersion.lastUpdated')}: ${lastUpdated}`
            : t('admin.appVersion.lastUpdatedNever')}
        </span>
        <Button
          onClick={onSaveClick}
          disabled={saving || hasErrors}
          className="bg-cyan-500 hover:bg-cyan-600 text-white flex items-center gap-2"
          data-testid={`app-version-save-${platform}`}
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? t('admin.appVersion.saving') : t('admin.appVersion.saveButton')}
        </Button>
      </div>
    </div>
  );
}

// ── Ana sayfa ──────────────────────────────────────────────────────────────────
export default function AdminAppVersion() {
  const { lang, t } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const [configs, setConfigs] = useState({});
  const [forms, setForms] = useState({});
  const [activeTab, setActiveTab] = useState('android');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [savingPlatform, setSavingPlatform] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false, platform: null, newVersion: null });

  // ── Sürüm dağılım telemetrisi (GET /admin/app-version/stats) — configs
  // fetch'inden BAĞIMSIZ ayrı bir veri kaynağı (bkz. VersionDistributionCard).
  const [statsWindowDays, setStatsWindowDays] = useState(7);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);
  const [staleFillPending, setStaleFillPending] = useState({ android: false, ios: false });
  const [staleFillRing, setStaleFillRing] = useState({ android: false, ios: false });
  const ringTimeoutsRef = useRef({});

  const fetchStats = useCallback(async (days) => {
    setStatsLoading(true);
    setStatsError(false);
    try {
      const res = await axios.get(`${API}/admin/app-version/stats`, {
        headers: authHeaders(),
        params: { days },
      });
      setStatsData(res.data);
    } catch {
      setStatsError(true);
    } finally {
      setStatsLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchStats(statsWindowDays); }, [fetchStats, statsWindowDays]);

  // Timeout'ları unmount'ta temizle (bkz. handleFillSuggested — 1.5sn ring).
  useEffect(() => () => {
    Object.values(ringTimeoutsRef.current).forEach((timeoutId) => clearTimeout(timeoutId));
  }, []);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/admin/app-version`, { headers: authHeaders() });
      const items = res.data.items || [];
      const nextConfigs = {};
      const nextForms = {};
      PLATFORMS.forEach((p) => {
        const item = items.find((i) => i.platform === p) || null;
        nextConfigs[p] = item;
        nextForms[p] = toFormState(item);
      });
      setConfigs(nextConfigs);
      setForms(nextForms);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const handleFieldChange = useCallback((platform, name, value) => {
    setForms((prev) => ({ ...prev, [platform]: { ...prev[platform], [name]: value } }));
  }, []);

  // "Önerilen sürümü doldur" — SADECE form state'ini doldurur, PUT ATMAZ
  // (bkz. VersionDistributionCard docstring'i — kasıtlı sürtünme, admin yine
  // de Kaydet'e basmak zorunda). 1.5sn'lik amber ring + kalıcı "unutma" notu.
  const handleFillSuggested = useCallback((platform, version) => {
    handleFieldChange(platform, 'latest_version', version);
    setStaleFillPending((prev) => ({ ...prev, [platform]: true }));
    setStaleFillRing((prev) => ({ ...prev, [platform]: true }));
    if (ringTimeoutsRef.current[platform]) clearTimeout(ringTimeoutsRef.current[platform]);
    ringTimeoutsRef.current[platform] = setTimeout(() => {
      setStaleFillRing((prev) => ({ ...prev, [platform]: false }));
    }, 1500);
  }, [handleFieldChange]);

  const doSave = useCallback(async (platform) => {
    if (!platform) return;
    setSavingPlatform(platform);
    try {
      const payload = toPayload(forms[platform]);
      const res = await axios.put(`${API}/admin/app-version/${platform}`, payload, { headers: authHeaders() });
      setConfigs((prev) => ({ ...prev, [platform]: res.data }));
      setForms((prev) => ({ ...prev, [platform]: toFormState(res.data) }));
      setStaleFillPending((prev) => ({ ...prev, [platform]: false }));
      toast.success(t('admin.appVersion.saveSuccess'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang) || t('admin.appVersion.saveError'));
    } finally {
      setSavingPlatform(null);
      setConfirmState({ open: false, platform: null, newVersion: null });
    }
  }, [forms, authHeaders, toast, t, lang]);

  const handleSaveClick = useCallback((platform) => {
    const form = forms[platform];
    if (!form) return;

    if (!isValidSemver(form.min_supported_version) || !isValidSemver(form.latest_version)) {
      toast.error(t('admin.appVersion.errorInvalidSemver'));
      return;
    }
    if (compareSemver(form.min_supported_version, form.latest_version) > 0) {
      toast.error(t('admin.appVersion.errorMinGtLatest'));
      return;
    }
    if (!isValidStoreUrl(form.store_url)) {
      toast.error(t('admin.appVersion.errorInvalidStoreUrl'));
      return;
    }

    const original = configs[platform];
    const minChanged = form.min_supported_version.trim() !== (original?.min_supported_version || '').trim();
    if (minChanged) {
      setConfirmState({ open: true, platform, newVersion: form.min_supported_version.trim() });
    } else {
      doSave(platform);
    }
  }, [forms, configs, doSave, toast, t]);

  return (
    <AdminLayout>
      <PageHeader
        title={t('admin.appVersion.title')}
        subtitle={t('admin.appVersion.subtitle')}
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: t('admin.appVersion.title') },
        ]}
      />

      {loading && <LoadingState variant="form" fields={6} />}

      {!loading && error && (
        <EmptyState
          icon={<Smartphone size={40} strokeWidth={1.5} />}
          title={t('admin.appVersion.loadError')}
          action={{ label: t('admin.appVersion.retry'), onClick: fetchConfigs }}
        />
      )}

      {!loading && !error && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="android" data-testid="app-version-tab-android">
              <Smartphone size={14} className="mr-1.5" />
              {t('admin.appVersion.tabAndroid')}
            </TabsTrigger>
            <TabsTrigger value="ios" data-testid="app-version-tab-ios">
              <Smartphone size={14} className="mr-1.5" />
              {t('admin.appVersion.tabIos')}
            </TabsTrigger>
          </TabsList>

          {PLATFORMS.map((platform) => (
            <TabsContent key={platform} value={platform} className="mt-4">
              <PlatformPanel
                platform={platform}
                config={configs[platform]}
                form={forms[platform] || toFormState(null)}
                onFieldChange={(name, value) => handleFieldChange(platform, name, value)}
                onSaveClick={() => handleSaveClick(platform)}
                saving={savingPlatform === platform}
                t={t}
                lang={lang}
                statsWindowDays={statsWindowDays}
                onStatsWindowChange={setStatsWindowDays}
                statsFromDate={fmtDateOnly(statsData?.from_date, lang)}
                statsToDate={fmtDateOnly(statsData?.to_date, lang)}
                statsLoading={statsLoading}
                statsError={statsError}
                onStatsRetry={() => fetchStats(statsWindowDays)}
                platformStats={statsData?.platforms?.find((p) => p.platform === platform) || null}
                onFillSuggested={handleFillSuggested}
                staleFillPending={staleFillPending[platform]}
                staleFillRing={staleFillRing[platform]}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}

      <LockConfirmDialog
        open={confirmState.open}
        onOpenChange={(o) => { if (!o) setConfirmState({ open: false, platform: null, newVersion: null }); }}
        newVersion={confirmState.newVersion}
        onConfirm={() => doSave(confirmState.platform)}
        loading={savingPlatform === confirmState.platform && confirmState.platform !== null}
        t={t}
      />
    </AdminLayout>
  );
}
